import { useCallback } from 'react';
import type {
  ProDocumentRow,
  ProDocumentUpdate,
} from '../../../../types/proDocuments';
import FileDropzone from '../../../../components/FileDropzone';

interface DocsUploadFormProps {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  // Metadata is set in the post-upload edit dialog, so onUpload is called with
  // an empty meta. Returns the new row so the parent can open the dialog.
  onUpload: (file: File, meta: ProDocumentUpdate) => Promise<ProDocumentRow | null>;
  // Fires ONCE per drop/pick once every file in the batch has finished
  // uploading (or failed). Parent uses this to open the edit dialog with all
  // newly uploaded rows in the file dropdown.
  onUploaded?: (rows: ProDocumentRow[]) => void;
}

/**
 * Pro-specific document uploader. Thin wrapper around the shared
 * {@link FileDropzone} that pins the row/meta types to the Pro document API and
 * keeps the long-standing `pronumber` data hook. The generic drag-drop logic
 * lives in FileDropzone so the quote Documents tab can reuse it.
 */
export default function DocsUploadForm({
  pronumber,
  onUpload,
  onUploaded,
}: DocsUploadFormProps) {
  // Pro sets metadata in a later edit dialog, so the upload happens with empty
  // meta here — preserves the existing `onUpload(file, {})` contract.
  const handleUpload = useCallback(
    (file: File) => onUpload(file, {}),
    [onUpload],
  );

  return (
    <FileDropzone<ProDocumentRow>
      onUpload={handleUpload}
      onUploaded={onUploaded}
      wrapperProps={{ 'data-pronumber': pronumber }}
    />
  );
}
